import { z } from "zod";
import { ExtractionExpressionSchema } from "./extraction-expression";
import { PublicNavigationUrlSchema } from "./public-url";
import { SearchLocalizationSchema } from "./search-localization";
import {
  EvidenceBundleSchema,
  type EvidenceBundle,
  EvidenceCallReceiptSchema,
  EvidenceComponentStateSchema,
  EvidenceErrorCodeSchema,
  EvidenceTimeScopeSchema,
  FieldAdoptionSchema,
} from "./evidence";

/**
 * 受控取数协议 `evidence-acquisition-v1`（统一技术实施方案 §6、§8、§9、§10）。
 *
 * 与旧协议的关系：`contextual-coaching-v1` / `local-coaching-v1` 的动作类型与限制**不变**。
 * 新动作只在新协议里实现，不往旧 `click` / `form` 字段里塞任意指令；凭据带协议域字段，
 * 新旧凭据不能互相接受（§13.3）。
 */

export const EVIDENCE_ACQUISITION_PROTOCOL = "evidence-acquisition-v1" as const;
export const EVIDENCE_ACQUISITION_API_PREFIX = "/api/v1/evidence-acquisition" as const;

/* -------------------------------------------------------------------------- */
/* §6.5 / §11.3 冻结的预算与输入上限                                             */
/* -------------------------------------------------------------------------- */

/**
 * 把搜索词翻成**目标市场语言**（P3-41，用户确认「不是直接使用，而是需要翻译」）。
 *
 * 为什么要一次单独的模型调用：四张初筛卡要拿一个"目标站上真正搜得到的词"去开搜索页，
 * 而商品页标题往往是中文（1688 货源页）。规则沿用既有的搜索语言规则
 * （按目标市场语义翻译、品牌/SKU/型号保留、不增添原指令没有的条件）。
 */
export const EvidenceSearchQueryRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  platform: z.string().trim().min(1).max(80),
  market: z.string().trim().max(80).optional(),
  /** 页面上给的原始词（通常是商品标题）：**逐字**送进去，不改写。 */
  productTitle: z.string().trim().min(1).max(300),
  category: z.string().trim().max(160).optional(),
}).strict();
export type EvidenceSearchQueryRequest = z.infer<typeof EvidenceSearchQueryRequestSchema>;

export const EvidenceSearchQueryResponseSchema = z.object({
  /** 原词：回显送入的那一份（便于界面并排显示"原词 → 实际检索词"）。 */
  originalQuery: z.string().min(1).max(300),
  query: z.string().trim().min(1).max(160),
  /** BCP47 语言标签；只做形状校验，不在这里另抄一份语言表。 */
  language: z.string().trim().min(2).max(35),
  basis: z.enum(["user_requested", "source_content", "target_market", "language_neutral"]),
  reason: z.string().trim().min(1).max(300),
  receipt: EvidenceCallReceiptSchema,
}).strict();
export type EvidenceSearchQueryResponse = z.infer<typeof EvidenceSearchQueryResponseSchema>;

/** Five valid reviews from at most ten candidates; reserve two future list samples. */
const reviewCandidates = 10, reviewListSampleAttempts = 2, reviewRecognitionAttempts = 2;
export const REVIEW_COLLECTION_LIMITS = {
  validProducts: 5,
  candidates: reviewCandidates,
  listSampleAttempts: reviewListSampleAttempts,
  recognitionAttemptsPerSample: reviewRecognitionAttempts,
  maxScreenshotCalls: (reviewCandidates + reviewListSampleAttempts) * reviewRecognitionAttempts,
} as const;

export const EVIDENCE_ACQUISITION_LIMITS = {
  /** 每段修改型原子步骤。 */
  modifiedStepsPerSegment: 3,
  maxConfirmedSegments: 6,
  maxModifiedStepsPerSession: 18,
  maxPlanningCalls: 6,
  maxReadBatches: 30,
  /**
   * 单条会话的截图识别调用上限。
   *
   * 从 **2** 提到 **6** 的理由是用户给的固定过程（2026-09-17）：
   * - 竞争 / 价格带：**两份结果**各一张（默认排序页、热卖排序页）＝ 2 张；
   * - 评论：热卖页 1 张 + 逐个商品详情页的评论标签（最多 5 个）＝ 至多 6 张。
   *
   * 原来 2 张是"截图兜底"时代的上限（一张图补一个缺字段），而固定过程把截图当**主路径**，
   * 2 张会在第二份结果时就撞上限，用户看到的是"截图识别达到上限 2"这种走到一半的失败。
   * 预算仍然是**每会话**的：每张卡各开自己的会话，所以这个上限不会跨卡累计。
   */
  maxScreenshotCalls: 6,
  maxScreenshotsPerCall: 2,
  sessionTimeoutMs: 600_000,
  stepWaitTimeoutMs: 15_000,
  /** 相同状态下修正同一失败目标的最多次数。 */
  maxRetriesPerTarget: 2,
  /** 单次观察进入模型的上限。 */
  maxObservationBlocks: 80,
  maxObservationTableRows: 200,
  maxObservationChars: 16_000,
  /** 最终市场正文上限（旧值保持不变）。 */
  maxMarketNarrativeChars: 8_000,
  maxScreenshotBytes: 8 * 1024 * 1024,
  maxScreenshotPixels: 16_000_000,
  maxRequestBytes: 24 * 1024 * 1024,
  planCredentialTtlSeconds: 600,
} as const;

export const ACQUISITION_ACTIONS = [
  "open_section",
  "activate_tab",
  "set_query",
  "choose_option",
  "set_checked",
  "set_multi_selection",
  "choose_cascade_level",
  "apply_query",
  "scroll_region",
  "next_results_page",
  "open_observed_link",
  "wait_for_state",
  "extract",
  "read_site_export",
  "web_search",
  "navigate_to_url",
] as const;
export const AcquisitionActionTypeSchema = z.enum(ACQUISITION_ACTIONS);
export type AcquisitionActionType = z.infer<typeof AcquisitionActionTypeSchema>;

/** §6.1：`extract` 是本地只读；`wait_for_state` 只等待，不改状态。**/
/**
 * 只读动作：不改页面状态，因此不消耗"修改步数"预算。
 * `read_site_export` 读的是**用户已选中的本地文件**，同样不改页面。
 */
export const ACQUISITION_READ_ONLY_ACTIONS = ["extract", "wait_for_state", "read_site_export", "web_search"] as const;

/**
 * 计入**读取批次**预算的动作。
 *
 * 只读 ≠ 不花成本：`extract` 读页面、`read_site_export` 读文件、`web_search` 读网络
 * （而且检索是**花钱**的外部调用），三者都要占读取预算，否则它们可以无限次执行。
 * `wait_for_state` 是只读但**不读取任何内容**（只等待），所以不占。
 *
 * 单一所有者：`Record<...>` 那头的判定读这里，界面与执行器不各写一份。
 */
export const ACQUISITION_READ_BUDGET_ACTIONS = ["extract", "read_site_export", "web_search"] as const;

export function consumesReadBudget(type: AcquisitionActionType): boolean {
  return (ACQUISITION_READ_BUDGET_ACTIONS as readonly string[]).includes(type);
}

/** 查询区域：搜索栏与筛选栏。"同一查询区域内"这类判断必须两侧同口径，所以放在契约层。 */
export const QUERY_REGION_KINDS = ["search_bar", "filter_bar"] as const;

/**
 * 动作能否落在**这个控件**上（用途证据判定）。客户端事前核对与服务端策略复核共用它，
 * 避免两处各判一次然后互相否决。
 *
 * 其中 `apply_query` 有一处**刻意的放宽**：允许提交"同一个查询输入框"。
 *
 * 为什么：真机上 Google Trends 的 `purposes` 里**没有任何 `query_apply`**，
 * 而契约把 `pressEnter` 固定为 `false`——三者叠加会让"提交查询"**根本无法表达**，
 * 模型只能反复 `set_query` 直到预算耗尽（真机 2 次失败 / 0 次纠回）。
 *
 * 放宽的边界同时满足三条，缺一不可：① 动作确实是 `apply_query`（提交仍是独立、需单独授权的
 * 第二段动作，两步治理不变）；② 目标是 `query_input`（**可证明**的搜索框，不是 unknown）；
 * ③ 它落在查询区域内。
 */
export function actionSatisfiesControlPurpose(input: {
  actionType: AcquisitionActionType;
  required: AcquisitionControlPurpose;
  control: { purpose: AcquisitionControlPurpose; binding: { regionRef?: string | undefined } };
  regions: ReadonlyArray<{ regionRef: string; kind: string }>;
}): boolean {
  if (input.control.purpose === input.required) return true;
  if (input.required === "unknown") return true;
  if (input.actionType !== "apply_query" || input.control.purpose !== "query_input") return false;
  const regionRef = input.control.binding.regionRef;
  return regionRef !== undefined
    && input.regions.some((region) => region.regionRef === regionRef && (QUERY_REGION_KINDS as readonly string[]).includes(region.kind));
}

export function isModifyingAcquisitionAction(type: AcquisitionActionType): boolean {
  return !(ACQUISITION_READ_ONLY_ACTIONS as readonly string[]).includes(type);
}

/**
 * §6.2 的拒绝信号：同一种控件不等于同一种权限。
 *
 * 这份清单**唯一所有者**在这里：页面侧（观察器判定 purpose）与服务端（策略复核）必须用同一份，
 * 否则会出现"页面以为可以点、服务端以为不行"或反过来更糟的"服务端放行了页面本不该认定的控件"。
 * 它只是**反向**判定——正向用途仍必须由角色/区域/表单语义/关联标签共同证明。
 */
export const ACQUISITION_DENY_SIGNALS = [
  "同意", "我同意", "接受", "授权", "授权访问", "协议", "隐私政策", "订阅",
  "agree", "accept", "authorize", "consent", "subscribe",
  "删除", "移除", "下架", "清空", "注销",
  "delete", "remove", "unpublish", "deactivate",
  "下单", "提交订单", "支付", "付款", "购买", "结算", "立即购买",
  "checkout", "place order", "pay", "buy now", "purchase", "confirm order",
  "签署", "签名", "签约", "sign", "e-sign",
  "发布", "上架", "提交审核", "publish", "list item",
  "保存", "save", "submit",
] as const;

/** 文本里出现任一拒绝信号即视为不可正向判定；空文本不算命中。 */
/**
 * **散文**检查专用的词表：只保留"不可逆承诺"那一类。
 *
 * 完整词表（`ACQUISITION_DENY_SIGNALS`）同时用于**控件名/标签**的判定——那里出现的
 * "清空/移除/删除"说的是**这个控件叫什么**，值得拦。
 * 但当同一份词表被拿去匹配模型的**自由文本理由**时，就变成了另一回事：
 * 真机上 2/3 次驱动尝试被它拦下，命中词是「清空」「移除」——
 * 而"清空搜索框再输入新关键词""移除当前关键词"正是**改查询**最自然的说法。
 *
 * 分界线是**文字没有能力**：散文只能表达"我打算做什么"，真正约束能力的是控件级判定
 * （`policy.ts` 的 `hasAcquisitionDenySignal([control.name, binding.label])`）与动作类型枚举。
 * 所以散文只留那些"一旦真的发生就不可逆"的承诺：支付/下单/发布/签署/同意/保存/提交。
 * 破坏类（删除/移除/清空/下架/注销）交给控件级判定——模型真要做这些事，
 * 它选中的控件名或标签会说出来。
 */
export const ACQUISITION_PROSE_DENY_SIGNALS = [
  "同意", "我同意", "接受", "授权", "授权访问", "协议", "隐私政策", "订阅",
  "agree", "accept", "authorize", "consent", "subscribe",
  "下单", "提交订单", "支付", "付款", "购买", "结算", "立即购买",
  "checkout", "place order", "pay", "buy now", "purchase", "confirm order",
  "签署", "签名", "签约", "sign", "e-sign",
  "发布", "上架", "提交审核", "publish", "list item",
  "保存", "save", "submit",
] as const;

/** 自由文本里命中的散文级禁用词；没有命中返回 undefined。 */
export function acquisitionProseDenySignalIn(text: string | null | undefined): string | undefined {
  if (!text) return undefined;
  const lowered = text.toLowerCase();
  return ACQUISITION_PROSE_DENY_SIGNALS.find((signal) => lowered.includes(signal));
}

export function hasAcquisitionDenySignal(text: string | null | undefined): boolean {
  return acquisitionDenySignalIn(text) !== undefined;
}

/**
 * 命中的**是哪一个**禁用信号；没有命中返回 undefined。
 *
 * 为什么需要它：`hasAcquisitionDenySignal` 只回答"是不是被拦了"，
 * 而一个 403 到底是"模型真想下单"还是"模型在理由里写了『清空搜索框』"，
 * 从布尔值上完全看不出来。真实探针里 2/3 次驱动尝试被这条规则拦下，
 * 而它的匹配对象是模型的**自由文本理由**、词表里又含「清空/移除/接受/保存」这类
 * 普通界面动词——所以必须能说出命中的那个词，才谈得上判断这是护栏还是误伤。
 */
export function acquisitionDenySignalIn(text: string | null | undefined): string | undefined {
  if (!text) return undefined;
  const lowered = text.toLowerCase();
  return ACQUISITION_DENY_SIGNALS.find((signal) => lowered.includes(signal));
}

/* -------------------------------------------------------------------------- */
/* §4.1 AcquisitionGoal                                                         */
/* -------------------------------------------------------------------------- */

export const AcquisitionPurposeSchema = z.enum(["task_verification", "form_assistance", "information_collection"]);
export type AcquisitionPurpose = z.infer<typeof AcquisitionPurposeSchema>;

export const AcquisitionFieldRequestSchema = z.object({
  field: z.string().trim().min(1).max(120),
  label: z.string().trim().min(1).max(160).optional(),
  required: z.boolean().default(true),
  unit: z.string().trim().min(1).max(40).optional(),
  entityKind: z.enum(["product", "sku", "review", "supplier", "query", "page", "other"]).optional(),
}).strict();
export type AcquisitionFieldRequest = z.infer<typeof AcquisitionFieldRequestSchema>;

/** 目标平台/国家属于本次任务；来源平台/国家属于某批资料，两者分别记录（§8.2）。 */
export const AcquisitionTargetScopeSchema = z.object({
  platform: z.string().trim().min(1).max(80).optional(),
  country: z.string().trim().min(1).max(80).optional(),
  category: z.string().trim().min(1).max(160).optional(),
  entityId: z.string().trim().min(1).max(160).optional(),
  dataTime: EvidenceTimeScopeSchema.optional(),
  sampleScope: z.string().trim().min(1).max(200).optional(),
}).strict();
export type AcquisitionTargetScope = z.infer<typeof AcquisitionTargetScopeSchema>;

export const AcquisitionCoverageGoalSchema = z.enum(["current_view", "specified_count", "filtered_result", "all_enumerated", "unknown"]);
export type AcquisitionCoverageGoal = z.infer<typeof AcquisitionCoverageGoalSchema>;

export const AcquisitionBudgetSchema = z.object({
  modifiedStepsPerSegment: z.number().int().min(1).max(EVIDENCE_ACQUISITION_LIMITS.modifiedStepsPerSegment).default(EVIDENCE_ACQUISITION_LIMITS.modifiedStepsPerSegment),
  maxConfirmedSegments: z.number().int().min(1).max(EVIDENCE_ACQUISITION_LIMITS.maxConfirmedSegments).default(EVIDENCE_ACQUISITION_LIMITS.maxConfirmedSegments),
  maxModifiedSteps: z.number().int().min(1).max(EVIDENCE_ACQUISITION_LIMITS.maxModifiedStepsPerSession).default(EVIDENCE_ACQUISITION_LIMITS.maxModifiedStepsPerSession),
  maxPlanningCalls: z.number().int().min(1).max(EVIDENCE_ACQUISITION_LIMITS.maxPlanningCalls).default(EVIDENCE_ACQUISITION_LIMITS.maxPlanningCalls),
  maxReadBatches: z.number().int().min(1).max(EVIDENCE_ACQUISITION_LIMITS.maxReadBatches).default(EVIDENCE_ACQUISITION_LIMITS.maxReadBatches),
  maxScreenshotCalls: z.number().int().min(0).max(REVIEW_COLLECTION_LIMITS.maxScreenshotCalls).default(EVIDENCE_ACQUISITION_LIMITS.maxScreenshotCalls),
  timeoutMs: z.number().int().min(1_000).max(EVIDENCE_ACQUISITION_LIMITS.sessionTimeoutMs).default(EVIDENCE_ACQUISITION_LIMITS.sessionTimeoutMs),
  /** 模型费用上限（积分），按既有价格配置向用户展示。 */
  maxCostPoints: z.number().int().nonnegative().optional(),
}).strict();
export type AcquisitionBudget = z.infer<typeof AcquisitionBudgetSchema>;

export const AcquisitionGoalSchema = z.object({
  purpose: AcquisitionPurposeSchema,
  objective: z.string().trim().min(1).max(300),
  requestedFields: z.array(AcquisitionFieldRequestSchema).min(1).max(40),
  targetScope: AcquisitionTargetScopeSchema,
  coverageGoal: AcquisitionCoverageGoalSchema,
  /** Explicit fixed review procedure; only this goal may reserve replacement-sample budget. */
  screenshotProcedure: z.literal("review_sample").optional(),
  /**
   * 先验链路的**引用**（方案 §7 C6）。刻意只带引用、不带链路本体：
   * 链路由服务端按键取用，客户端无法篡改先验；契约层也不会因此出现
   * `evidence-acquisition ↔ execution-recipe` 的循环 import。引用本身不授予任何权限。
   */
  recipeRef: z.string().min(1).max(160).optional(),
  /**
   * P2-1：这次取数服务于**哪个子任务节点**（方案 §7 C6）。
   *
   * 只带标识、不带节点本体：节点属于任务树（服务端权威），目标里放一份副本
   * 就会出现"任务改了、目标里那份没改"的分叉。
   *
   * 刻意**没有** `contextRevision?`（C6 里列了，但这里没加）：
   * 它要标记"口径变化、跨 revision 的证据不混算"，而我目前没有一个**诚实的生产者**——
   * 拿任务 revision 顶替会把同一个口径切成很多份，比不加更糟。
   * 按 P1-3 的先例：字段与生产者一起加，不留只写不读的死字段。
   */
  subtaskNodeId: z.string().trim().min(1).max(160).optional(),
  allowedSites: z.array(z.string().trim().min(1).max(240)).max(20).default([]),
  budget: AcquisitionBudgetSchema,
  successCriteria: z.array(z.string().trim().min(1).max(240)).min(1).max(12),
  forbiddenActions: z.array(AcquisitionActionTypeSchema).max(ACQUISITION_ACTIONS.length).default([]),
  /** 缺目标国家/关键类目/用户真实资料时先澄清，不从当前网页随意推断目标。 */
  clarification: z.array(z.string().trim().min(1).max(200)).max(8).default([]),
}).strict().superRefine((value, context) => {
  if (value.screenshotProcedure === "review_sample" && value.purpose !== "information_collection") {
    context.addIssue({ code: "custom", path: ["screenshotProcedure"], message: "评论补样只适用于信息采集" });
  }
  if (value.screenshotProcedure !== "review_sample" && value.budget.maxScreenshotCalls > EVIDENCE_ACQUISITION_LIMITS.maxScreenshotCalls) {
    context.addIssue({ code: "custom", path: ["budget", "maxScreenshotCalls"], message: "非评论采集不得超过6次截图识别" });
  }
  if (value.purpose === "form_assistance" && value.coverageGoal === "all_enumerated") {
    context.addIssue({ code: "custom", path: ["coverageGoal"], message: "填表不适用全量枚举目标" });
  }
  const forbidden = new Set(value.forbiddenActions);
  if (forbidden.has("extract")) {
    context.addIssue({ code: "custom", path: ["forbiddenActions"], message: "extract 是本地只读，不能设为禁止动作" });
  }
});
export type AcquisitionGoal = z.infer<typeof AcquisitionGoalSchema>;

/* -------------------------------------------------------------------------- */
/* §9.1 状态机                                                                  */
/* -------------------------------------------------------------------------- */

export const AcquisitionSessionStateSchema = z.enum([
  "created",
  "observing",
  "planning",
  "awaiting_confirmation",
  "running",
  "waiting_user",
  "awaiting_screenshot_consent",
  "partial",
  "completed",
  "stopped",
  "failed",
  "interrupted",
]);
export type AcquisitionSessionState = z.infer<typeof AcquisitionSessionStateSchema>;

export const AcquisitionActionStateSchema = z.enum([
  "planned",
  "confirmed",
  "authorizing",
  "dispatching",
  "observing",
  "succeeded",
  "failed",
  "unknown",
]);
export type AcquisitionActionState = z.infer<typeof AcquisitionActionStateSchema>;

export const AcquisitionBudgetUsageSchema = z.object({
  planningCalls: z.number().int().nonnegative().default(0),
  readBatches: z.number().int().nonnegative().default(0),
  screenshotCalls: z.number().int().nonnegative().default(0),
  confirmedSegments: z.number().int().nonnegative().default(0),
  modifiedSteps: z.number().int().nonnegative().default(0),
  costPoints: z.number().int().nonnegative().default(0),
  startedAt: z.string(),
}).strict();
export type AcquisitionBudgetUsage = z.infer<typeof AcquisitionBudgetUsageSchema>;

export const AcquisitionSessionSchema = z.object({
  sessionId: z.string().min(1).max(160),
  protocol: z.literal(EVIDENCE_ACQUISITION_PROTOCOL),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
  taskId: z.string().min(1).max(160).optional(),
  draftId: z.string().min(1).max(160).optional(),
  goal: AcquisitionGoalSchema,
  state: AcquisitionSessionStateSchema,
  revision: z.number().int().nonnegative(),
  budgetUsage: AcquisitionBudgetUsageSchema,
  /** 已确认段数达到上限后不再接受新段，但保留已有成果。 */
  stopRequested: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
}).strict().superRefine((value, context) => {
  const budget = value.goal.budget;
  const usage = value.budgetUsage;
  if (usage.modifiedSteps > budget.maxModifiedSteps) {
    context.addIssue({ code: "custom", path: ["budgetUsage", "modifiedSteps"], message: "修改步骤已超出会话上限" });
  }
  if (usage.confirmedSegments > budget.maxConfirmedSegments) {
    context.addIssue({ code: "custom", path: ["budgetUsage", "confirmedSegments"], message: "已确认段数已超出上限" });
  }
  if (usage.readBatches > budget.maxReadBatches) {
    context.addIssue({ code: "custom", path: ["budgetUsage", "readBatches"], message: "读取批次已超出上限" });
  }
});
export type AcquisitionSession = z.infer<typeof AcquisitionSessionSchema>;

/* -------------------------------------------------------------------------- */
/* §6.4 控件引用与断言                                                          */
/* -------------------------------------------------------------------------- */

export const ControlBindingSchema = z.object({
  controlRef: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160),
  tabId: z.number().int().nonnegative(),
  frameId: z.number().int().nonnegative().default(0),
  documentKey: z.string().min(1).max(160),
  regionRef: z.string().min(1).max(160).optional(),
  role: z.string().max(60).optional(),
  label: z.string().max(200).optional(),
  /** 引用生成时的属性摘要；执行前重新核对，重渲染后旧引用失效。 */
  stateDigest: z.string().regex(/^[a-f\d]{8,64}$/),
}).strict();
export type ControlBinding = z.infer<typeof ControlBindingSchema>;

export const AcquisitionAssertionSchema = z.object({
  kind: z.enum([
    "exists",
    "visible",
    "enabled",
    "checked",
    "text_equals",
    "text_contains",
    "selected_option",
    "count_at_least",
    "url_matches_observed",
    "loading_finished",
    "result_container_updated",
  ]),
  controlRef: z.string().min(1).max(160).optional(),
  regionRef: z.string().min(1).max(160).optional(),
  value: z.string().max(400).optional(),
  count: z.number().int().nonnegative().max(10_000).optional(),
}).strict().superRefine((value, context) => {
  if ((value.kind === "exists" || value.kind === "visible" || value.kind === "enabled" || value.kind === "checked") === false) return;
  if (!value.controlRef && !value.regionRef) {
    context.addIssue({ code: "custom", path: ["controlRef"], message: "状态断言必须指向控件或区域" });
  }
});
export type AcquisitionAssertion = z.infer<typeof AcquisitionAssertionSchema>;

/**
 * 断言的三态判定（§6.4 后置条件）。
 *
 * 刻意**不是布尔**：从一份观察里判不出来的断言（例如本地观察没有"当前选中项"这个字段）
 * 只能说 `unknown`，绝不能被当成失败 —— 那会把已经成功的动作误报成失败。
 */
export type AssertionVerdict = "pass" | "fail" | "unknown";

/**
 * 在**一份观察**上判定一条断言。
 *
 * 后置条件必须在**动作之后重新观察到的**现场上判定：拿动作前的现场判"结果出现了吗"必然误判。
 *
 * 当前能判的：`exists` / `visible` / `enabled` / `checked` / `text_contains` / `text_equals` /
 * `url_matches_observed`。判不了的（`selected_option` / `count_at_least` / `loading_finished` /
 * `result_container_updated`）一律 `unknown` —— 本地观察目前没有承载这些信息的字段，
 * 宁可说"不知道"，也不猜一个结论出来。
 *
 * 文本类断言的已知粗化：观察里没有"文本块属于哪个区域"的关联，
 * 所以 `text_*` 是在**整份观察的文本**上判定，而不是断言指向的那个区域。这一条写在这里，
 * 免得被误当成"区域级文本断言已经支持"。
 */
export function evaluateAcquisitionAssertion(assertion: AcquisitionAssertion, observation: PageObservation): AssertionVerdict {
  const control = assertion.controlRef ? observation.controls.find((item) => item.controlRef === assertion.controlRef) : undefined;
  const region = assertion.regionRef ? observation.regions.find((item) => item.regionRef === assertion.regionRef) : undefined;

  switch (assertion.kind) {
    case "exists":
      // 断言自带 superRefine：状态类断言必须指向控件或区域，所以这里至少有一侧。
      if (assertion.controlRef) return control ? "pass" : "fail";
      return region ? "pass" : "fail";
    case "visible":
      if (!control) return "fail";
      return control.visible && !control.covered ? "pass" : "fail";
    case "enabled":
      if (!control) return "fail";
      return control.disabled ? "fail" : "pass";
    case "checked":
      if (!control) return "fail";
      // 控件类型没有"勾选"这个概念时无法判定，不猜。
      return control.checked === undefined ? "unknown" : control.checked ? "pass" : "fail";
    case "text_contains":
    case "text_equals": {
      if (typeof assertion.value !== "string") return "unknown";
      const text = observationText(observation);
      if (assertion.kind === "text_contains") return text.includes(assertion.value) ? "pass" : "fail";
      return text.trim() === assertion.value.trim() ? "pass" : "fail";
    }
    case "url_matches_observed": {
      if (typeof assertion.value !== "string") return "unknown";
      const strip = (value: string) => value.replace(/[#?].*$/, "").replace(/\/$/, "");
      return strip(observation.document.url) === strip(assertion.value) ? "pass" : "fail";
    }
    default:
      return "unknown";
  }
}

/** 整份观察的可读文本（文本块 + 表格），用于文本类断言。 */
function observationText(observation: PageObservation): string {
  const blocks = observation.textBlocks.map((block) => block.text);
  const tables = observation.tables.flatMap((table) => [...table.headers, ...table.rows.flat()]);
  return [...blocks, ...tables].join("\n");
}

/* -------------------------------------------------------------------------- */
/* 模型输出的**散文**上限（归一器与 schema 共用同一份常量）                          */
/* -------------------------------------------------------------------------- */

/**
 * 为什么这些数字要有名字：模型输出的机械归一器（`apps/api/.../action-identity.ts`）
 * 必须知道"截到多少"，而它一旦自己抄一份数字，就会和 schema 悄悄分叉——
 * 真机已经吃过一次：动作级 `reason` 归一了 300，**提案级** `reason`（400）却漏着，
 * 于是一个超长的提案理由让整段规划作废、再花一次钱重抽（会话 `6d052941` 16:42:01）。
 *
 * 边界：这里只放**解释性文字**。`query`/`optionLabel`/`expectValue`/`url` 这类
 * **内容**字段一个字都不许截——截短一个查询词就是另一个查询。
 */
export const EVIDENCE_ACTION_REASON_MAX = 300;
export const EVIDENCE_PROPOSAL_REASON_MAX = 400;
/** 结论/预期/疑问这类"说法"：`expectedOutcome`、`expectation`、`mayStillNotConfirm`、`questions`。 */
export const EVIDENCE_PROPOSAL_NOTE_MAX = 300;
/** "已经试过哪些读法"这类自述。 */
export const EVIDENCE_ATTEMPTED_NOTE_MAX = 200;
/** 字段名（`missingFields`/`coveredFields`/`expectedFields`）：是标识，不是散文。 */
export const EVIDENCE_FIELD_NAME_MAX = 120;

/* -------------------------------------------------------------------------- */
/* §6.1 受控动作                                                                */
/* -------------------------------------------------------------------------- */

const ActionBaseFields = {
  actionId: z.string().uuid(),
  reason: z.string().trim().min(1).max(EVIDENCE_ACTION_REASON_MAX),
  preconditions: z.array(AcquisitionAssertionSchema).max(6).default([]),
  postconditions: z.array(AcquisitionAssertionSchema).max(6).default([]),
};

const action = <T extends z.ZodRawShape>(shape: T) => z.object({ ...ActionBaseFields, ...shape }).strict();

export const AcquisitionActionSchema = z.discriminatedUnion("type", [
  action({
    type: z.literal("open_section"),
    controlRef: ControlBindingSchema,
    /** 预期展开状态必须明确，不是任意按钮点击。 */
    expectedExpanded: z.literal(true),
  }),
  action({
    type: z.literal("activate_tab"),
    controlRef: ControlBindingSchema,
    expectedActive: z.literal(true),
  }),
  action({
    type: z.literal("set_query"),
    controlRef: ControlBindingSchema,
    value: z.string().max(400),
    /** 不默认按 Enter：查询必须由 apply_query 显式确认。 */
    pressEnter: z.literal(false).default(false),
    searchLocalization: SearchLocalizationSchema.optional(),
  }),
  action({
    type: z.literal("choose_option"),
    controlRef: ControlBindingSchema,
    optionRef: z.string().min(1).max(160).optional(),
    optionLabel: z.string().trim().min(1).max(200),
    expectValue: z.string().max(200),
  }),
  action({
    type: z.literal("set_checked"),
    controlRef: ControlBindingSchema,
    /** 指定目标状态，不用盲目 toggle。 */
    checked: z.boolean(),
  }),
  action({
    type: z.literal("set_multi_selection"),
    controlRef: ControlBindingSchema,
    targetChecked: z.array(z.object({ optionRef: z.string().min(1).max(160), checked: z.boolean() }).strict()).min(1).max(40),
  }),
  action({
    type: z.literal("choose_cascade_level"),
    controlRef: ControlBindingSchema,
    level: z.number().int().min(1).max(8),
    optionLabel: z.string().trim().min(1).max(200),
    optionRef: z.string().min(1).max(160).optional(),
    /** 下一层是否已观察；未观察时不得提前签发子项选择器。 */
    nextLevelObserved: z.boolean().default(false),
  }),
  action({
    type: z.literal("apply_query"),
    controlRef: ControlBindingSchema,
    /** 必须被识别为查询操作；不能通用提交任意 form。 */
    queryEvidence: z.string().trim().min(1).max(200),
    expectResultContainerRef: z.string().min(1).max(160).optional(),
  }),
  action({
    type: z.literal("scroll_region"),
    regionRef: z.string().min(1).max(160),
    direction: z.enum(["down", "up"]),
    distancePx: z.number().int().min(1).max(20_000),
  }),
  action({
    type: z.literal("next_results_page"),
    controlRef: ControlBindingSchema,
    expectedPage: z.number().int().min(2).max(500),
    /** 已见页面指纹，用于防止重复页与无限循环。 */
    seenPageSignatures: z.array(z.string().min(1).max(160)).max(100).default([]),
  }),
  action({
    type: z.literal("open_observed_link"),
    controlRef: ControlBindingSchema,
    /** 只能打开观察记录里已有的链接，不执行模型生成的任意 URL。 */
    observedUrl: z.string().max(2_048),
    expectedDocumentKey: z.string().min(1).max(160).optional(),
  }),
  action({
    type: z.literal("wait_for_state"),
    assertion: AcquisitionAssertionSchema,
    timeoutMs: z.number().int().min(100).max(EVIDENCE_ACQUISITION_LIMITS.stepWaitTimeoutMs),
  }),
  action({
    type: z.literal("extract"),
    expression: ExtractionExpressionSchema,
    /** 只读，计入读取预算。 */
    readonly: z.literal(true).default(true),
  }),
  action({
    type: z.literal("navigate_to_url"),
    /**
     * 定向取数的**第 1 级**：去"这个信号的数据所在站点"。
     *
     * 与 `open_observed_link` 的区别是本质的：那条只能打开**页面上已观察到**的链接，
     * 而四维信号的数据（需求/竞争/价格带）根本不在当前页——必须能去别的站点。
     *
     * 安全口径（三点，缺一不可）：
     * 1. 地址必须过**公开性判定**，与 `open_url` 用同一个所有者（`public-url.ts`），不另写一套；
     * 2. 域必须落在本次会话的 `allowedSites` 内——那份清单**就是用户同意过的范围**
     *    （服务端策略强制，见 `policy.ts`），所以"首次访问新域"的确认发生在**开会话时**，
     *    粒度是**域**（方案 §3 R11），不是每次导航都问一遍；
     * 3. 它**改变页面**，因此计入"修改步"预算，不是只读动作。
     */
    url: PublicNavigationUrlSchema,
  }),
  action({
    type: z.literal("web_search"),
    /**
     * 检索词。**结果只是线索级**，不是政策/合规证据（§3 R13）：
     * 开放网络上的教程/blog 能告诉我们"怎么做"，但不能当作规则依据。
     */
    query: z.string().trim().min(1).max(300),
    /** 只要前几条；链路与线索都不需要长尾。 */
    maxResults: z.number().int().min(1).max(8).default(5),
    /** 只读：不改页面状态，但计入读取预算（它确实花了一次外部调用）。 */
    readonly: z.literal(true).default(true),
  }),
  action({
    type: z.literal("read_site_export"),
    /**
     * **用户显式选中的**导出文件标识（§P0-3）。
     * 没有它就不读——本动作绝不自己去磁盘上找文件、也绝不触发任何下载。
     */
    fileRef: z.string().trim().min(1).max(160),
    /** 解析出的表格引用名，供后续 `extract` 按表格取值。 */
    tableRef: z.string().trim().min(1).max(160),
    caption: z.string().trim().min(1).max(200).optional(),
    /** 只读：不改页面状态，但要计入读取预算。 */
    readonly: z.literal(true).default(true),
  }),
]);
export type AcquisitionAction = z.infer<typeof AcquisitionActionSchema>;

/**
 * §6.3：多选集合中的每次实际控件修改、级联每层选择都计为原子步骤，
 * 不能通过"批量"绕过步数限制。所以这里按实际状态变更数展开，而不是按动作条数。
 */
export function countModifiedSteps(actions: readonly AcquisitionAction[]): number {
  return actions.reduce((total, item) => {
    if (!isModifyingAcquisitionAction(item.type)) return total;
    if (item.type === "set_multi_selection") return total + item.targetChecked.length;
    if (item.type === "choose_cascade_level") return total + 1;
    return total + 1;
  }, 0);
}

export function modifyingActions(actions: readonly AcquisitionAction[]): AcquisitionAction[] {
  return actions.filter((item) => isModifyingAcquisitionAction(item.type));
}

/* -------------------------------------------------------------------------- */
/* §4.2 每轮决策：AcquisitionProposal                                            */
/* -------------------------------------------------------------------------- */

export const AcquisitionBranchSchema = z.enum(["extract", "interact", "wait", "request_screenshot", "clarify", "complete", "stop"]);
export type AcquisitionBranch = z.infer<typeof AcquisitionBranchSchema>;

/**
 * 只读、由扩展在**规划阶段自动执行**的分支：既不需要也不接受用户确认。
 *
 * 这张表是**唯一所有者**——界面（要不要出确认卡）、本地状态机（要不要置"等确认"）、
 * 循环（谁来执行）三处都必须读它。真机吃过一次三方口径不一致的亏（会话 `6d052941`）：
 * 界面注释说"只读分支自动执行"，循环里只实现了 `extract`，而本地状态机把**所有**分支
 * 都置成 `awaiting_confirmation`——于是 `wait` 提案的界面表现是
 * 「等你确认本段」＋没有卡片＋没有执行者，会话就停在那里了。
 *
 * 一旦这里增加分支，执行侧必须有对应的执行者；否则宁可停住并如实说明，
 * 也不能让它静静停在"等确认"上（`use-evidence-acquisition` 里有这条兜底）。
 */
export const AUTO_EXECUTED_PROPOSAL_BRANCHES = ["extract", "wait"] as const;

/** 这个分支要不要用户参与（确认或知情）。 */
export function proposalRequiresUser(branch: AcquisitionBranch): boolean {
  return !(AUTO_EXECUTED_PROPOSAL_BRANCHES as readonly AcquisitionBranch[]).includes(branch);
}

const ProposalBase = {
  reason: z.string().trim().min(1).max(EVIDENCE_PROPOSAL_REASON_MAX),
  /** 依据引用：只能引用本次观察里出现过的区域/控件。 */
  evidenceRefs: z.array(z.string().min(1).max(160)).max(40).default([]),
  /**
   * 与先验链路不一致时的说明（方案 §8 P2-5）。
   * 先验是起点而不是脚本：观察与先验冲突时以**观察**为准，但必须留下偏离原因，
   * 否则"每次都绕开先验"会变成一个没人能发现的静默行为。
   */
  recipeDeviation: z.string().trim().max(300).optional(),
  /** 用户明确指定链路时回显当前语义步骤；服务端与冻结的步骤逐字段比对。 */
  recipeStep: z.object({
    order: z.number().int().positive(),
    intent: z.string().min(1).max(300),
    successCheck: z.string().max(200),
  }).strict().optional(),
};

export const AcquisitionProposalSchema = z.discriminatedUnion("branch", [
  z.object({
    ...ProposalBase,
    branch: z.literal("extract"),
    expression: ExtractionExpressionSchema,
    expectedFields: z.array(z.string().trim().min(1).max(EVIDENCE_FIELD_NAME_MAX)).min(1).max(40),
    missingFields: z.array(z.string().trim().min(1).max(EVIDENCE_FIELD_NAME_MAX)).max(40).default([]),
  }).strict(),
  z.object({
    ...ProposalBase,
    branch: z.literal("interact"),
    /** 模型只规划下一段，最多 3 个改变网页状态的原子步骤。 */
    actions: z.array(AcquisitionActionSchema).min(1).max(ACQUISITION_ACTIONS.length),
    expectedOutcome: z.string().trim().min(1).max(EVIDENCE_PROPOSAL_NOTE_MAX),
  }).strict().superRefine((value, context) => {
    const steps = countModifiedSteps(value.actions);
    if (steps < 1) context.addIssue({ code: "custom", path: ["actions"], message: "interact 段必须包含至少一个状态变更动作" });
    if (steps > EVIDENCE_ACQUISITION_LIMITS.modifiedStepsPerSegment) {
      context.addIssue({ code: "custom", path: ["actions"], message: `本段修改步骤不得超过 ${EVIDENCE_ACQUISITION_LIMITS.modifiedStepsPerSegment} 步` });
    }
    const ids = value.actions.map((item) => item.actionId);
    if (new Set(ids).size !== ids.length) context.addIssue({ code: "custom", path: ["actions"], message: "动作 ID 重复" });
  }),
  z.object({
    ...ProposalBase,
    branch: z.literal("wait"),
    assertion: AcquisitionAssertionSchema,
    timeoutMs: z.number().int().min(100).max(EVIDENCE_ACQUISITION_LIMITS.stepWaitTimeoutMs),
  }).strict(),
  z.object({
    ...ProposalBase,
    branch: z.literal("request_screenshot"),
    missingFields: z.array(z.string().trim().min(1).max(EVIDENCE_FIELD_NAME_MAX)).min(1).max(20),
    /** 已尝试过哪些允许的结构化读取方式。 */
    attempted: z.array(z.string().trim().min(1).max(EVIDENCE_ATTEMPTED_NOTE_MAX)).min(1).max(12),
    targetRegionRef: z.string().min(1).max(160),
    expectation: z.string().trim().min(1).max(EVIDENCE_PROPOSAL_NOTE_MAX),
    mayStillNotConfirm: z.string().trim().min(1).max(EVIDENCE_PROPOSAL_NOTE_MAX),
  }).strict(),
  z.object({
    ...ProposalBase,
    branch: z.literal("clarify"),
    questions: z.array(z.string().trim().min(1).max(EVIDENCE_PROPOSAL_NOTE_MAX)).min(1).max(6),
    savedBundleRef: z.string().min(1).max(160).optional(),
  }).strict(),
  z.object({
    ...ProposalBase,
    branch: z.literal("complete"),
    coveredFields: z.array(z.string().trim().min(1).max(120)).max(40).default([]),
    missingFields: z.array(z.string().trim().min(1).max(EVIDENCE_FIELD_NAME_MAX)).max(40).default([]),
    coverage: z.enum(["current_view", "specified_count", "filtered_result", "all_enumerated", "unknown"]),
  }).strict(),
  z.object({
    ...ProposalBase,
    branch: z.literal("stop"),
    code: EvidenceErrorCodeSchema,
    retainedResults: z.string().max(300).optional(),
  }).strict(),
]);
export type AcquisitionProposal = z.infer<typeof AcquisitionProposalSchema>;

/* -------------------------------------------------------------------------- */
/* §5 / §8.1 PageObservation                                                    */
/* -------------------------------------------------------------------------- */

const SafeObservationUrlSchema = z.string().max(8_192).refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}, "只接受不含账号密码的 HTTPS 地址");

export const ObservationDocumentSchema = z.object({
  documentKey: z.string().min(1).max(160),
  tabId: z.number().int().nonnegative(),
  frameId: z.number().int().nonnegative().default(0),
  url: SafeObservationUrlSchema,
  title: z.string().max(500),
  domain: z.string().max(240),
  capturedAt: z.string(),
  visible: z.boolean(),
}).strict();
export type ObservationDocument = z.infer<typeof ObservationDocumentSchema>;

export const ObservationRegionSchema = z.object({
  regionRef: z.string().min(1).max(160),
  kind: z.enum(["data_table", "chart", "filter_bar", "tab_list", "cascade_menu", "detail_panel", "review_list", "search_bar", "pagination", "other"]),
  label: z.string().max(200).optional(),
  blockCount: z.number().int().nonnegative().default(0),
}).strict();
export type ObservationRegion = z.infer<typeof ObservationRegionSchema>;

/** 控件用途枚举（观察器正向判定的取值）。 */
export const AcquisitionControlPurposeSchema = z.enum(["query_input", "query_apply", "option_select", "filter_checkbox", "section_toggle", "tab_select", "pagination", "detail_link", "scroll_container", "unknown"]);
export type AcquisitionControlPurpose = z.infer<typeof AcquisitionControlPurposeSchema>;

export const ObservationControlSchema = z.object({
  controlRef: z.string().min(1).max(160),
  binding: ControlBindingSchema,
  tag: z.string().max(40),
  role: z.string().max(60).optional(),
  name: z.string().max(200).optional(),
  visible: z.boolean(),
  disabled: z.boolean(),
  checked: z.boolean().optional(),
  /** 是否被遮挡：遮挡时不得当作可操作目标。 */
  covered: z.boolean().default(false),
  /** 已审核的适配器给出的正向用途判定；没有判定就进 waiting_user。 */
  purpose: AcquisitionControlPurposeSchema,
  adapterId: z.string().max(120).optional(),
}).strict();
export type ObservationControl = z.infer<typeof ObservationControlSchema>;

export const ObservationTableSchema = z.object({
  tableRef: z.string().min(1).max(160),
  caption: z.string().max(200).optional(),
  headers: z.array(z.string().max(160)).max(80),
  rows: z.array(z.array(z.string().max(2_000)).max(80)).max(EVIDENCE_ACQUISITION_LIMITS.maxObservationTableRows),
  rowCount: z.number().int().nonnegative(),
  truncated: z.boolean().default(false),
}).strict();
export type ObservationTable = z.infer<typeof ObservationTableSchema>;

export const ObservationTextBlockSchema = z.object({
  blockRef: z.string().min(1).max(160),
  regionRef: z.string().min(1).max(160).optional(),
  text: z.string().max(2_000),
  /**
   * 可读 SVG/ARIA 文本也走这里，先于截图（§5.1 优先级 3）。
   *
   * `web_research` 是**线索级**：来自开放网络检索，不是页面读到的内容。
   * 它必须与页面文本区分开，否则"网上看到的说法"会被当成"页面上写着的事实"（§3 R13）。
   */
  kind: z.enum(["text", "svg_text", "aria", "shadow_text", "web_research"]).default("text"),
  /**
   * 出处。**只有线索级文本需要它**，所以可选——给页面文本硬塞一个空数组，
   * 会让人以为"页面文本也有出处，只是空的"。
   * 线索级没有出处则直接拒绝（见下方 refine）。
   */
  sources: z.array(z.object({
    title: z.string().max(200),
    url: z.string().max(2_048),
  }).strict()).max(8).optional(),
}).strict().superRefine((value, context) => {
  if (value.kind === "web_research" && (value.sources?.length ?? 0) === 0) {
    context.addIssue({ code: "custom", path: ["sources"], message: "线索级文本必须带来源：没有出处的线索不能进入现场" });
  }
});
export type ObservationTextBlock = z.infer<typeof ObservationTextBlockSchema>;

/**
 * 线索级文本的种类（§3 R13）。**单一所有者**：任何"把现场文本当证据"的地方
 * 都要用它把这些块排除掉，否则开放网络的内容会冒充页面读到的事实。
 */
export const LEAD_LEVEL_TEXT_KINDS = ["web_research"] as const;

export function isLeadLevelTextBlock(block: Pick<ObservationTextBlock, "kind">): boolean {
  return (LEAD_LEVEL_TEXT_KINDS as readonly string[]).includes(block.kind);
}

/**
 * 最小脱敏现场。区块/表格行/字符数都有上限（§11.3），
 * 超限必须显式标记 truncated，不得静默丢弃关键依据。
 */
export const PageObservationSchema = z.object({
  observationId: z.string().min(1).max(160),
  snapshotId: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160),
  document: ObservationDocumentSchema,
  regions: z.array(ObservationRegionSchema).max(EVIDENCE_ACQUISITION_LIMITS.maxObservationBlocks).default([]),
  controls: z.array(ObservationControlSchema).max(EVIDENCE_ACQUISITION_LIMITS.maxObservationBlocks).default([]),
  tables: z.array(ObservationTableSchema).max(20).default([]),
  textBlocks: z.array(ObservationTextBlockSchema).max(EVIDENCE_ACQUISITION_LIMITS.maxObservationBlocks).default([]),
  components: z.array(EvidenceComponentStateSchema).max(40).default([]),
  /** 当前筛选状态的人类可读摘要，用于说明本次数据的适用范围。 */
  filterState: z.array(z.string().max(160)).max(20).default([]),
  characterCount: z.number().int().nonnegative().default(0),
  truncated: z.boolean().default(false),
}).strict().superRefine((value, context) => {
  const totalChars = value.textBlocks.reduce((sum, block) => sum + block.text.length, 0)
    + value.tables.reduce((sum, table) => sum + table.headers.join("").length + table.rows.reduce((rowSum, row) => rowSum + row.join("").length, 0), 0);
  if (totalChars > EVIDENCE_ACQUISITION_LIMITS.maxObservationChars && !value.truncated) {
    context.addIssue({ code: "custom", path: ["truncated"], message: "超过字符上限必须显式标记 truncated" });
  }
});
export type PageObservation = z.infer<typeof PageObservationSchema>;

/* -------------------------------------------------------------------------- */
/* §10 凭据：协议域隔离                                                        */
/* -------------------------------------------------------------------------- */

export const EVIDENCE_PLAN_SIGNING_ALGORITHM = "hmac-sha256" as const;

/**
 * 载荷带 `protocol` 与 `purpose`：旧 `local-coaching-v1` 凭据没有这两个字段，
 * 因此无法被本协议接受，反之亦然（§13.3 协议域隔离）。
 */
export const EvidencePlanCredentialPayloadSchema = z.object({
  protocol: z.literal(EVIDENCE_ACQUISITION_PROTOCOL),
  actor: z.string().min(1).max(120),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
  sessionId: z.string().min(1).max(160),
  purpose: AcquisitionPurposeSchema,
  planVersion: z.string().min(1).max(80),
  actionIds: z.array(z.string().uuid()).min(1).max(ACQUISITION_ACTIONS.length),
  /** 已确认参数集的摘要；本地改参数会算出不同哈希，因此不能沿用旧许可。 */
  parameterHash: z.string().regex(/^[a-f\d]{64}$/),
  evidenceVersion: z.string().min(1).max(80),
  revision: z.number().int().nonnegative(),
  issuedAt: z.string().min(1).max(40),
  expiresAt: z.string().min(1).max(40),
}).strict();
export type EvidencePlanCredentialPayload = z.infer<typeof EvidencePlanCredentialPayloadSchema>;

export const EvidencePlanCredentialSchema = z.object({
  keyId: z.string().min(1).max(80),
  payload: z.string().min(1).max(16_384),
  digest: z.string().min(1).max(200),
  signature: z.string().min(1).max(200),
}).strict();
export type EvidencePlanCredential = z.infer<typeof EvidencePlanCredentialSchema>;

/* -------------------------------------------------------------------------- */
/* §10 接口契约                                                                */
/* -------------------------------------------------------------------------- */

export const EvidenceCapabilitiesSchema = z.object({
  enabled: z.boolean(),
  protocol: z.literal(EVIDENCE_ACQUISITION_PROTOCOL),
  actions: z.array(AcquisitionActionTypeSchema).min(1),
  limits: z.object({
    modifiedStepsPerSegment: z.number().int().positive(),
    maxConfirmedSegments: z.number().int().positive(),
    maxModifiedStepsPerSession: z.number().int().positive(),
    maxPlanningCalls: z.number().int().positive(),
    maxReadBatches: z.number().int().positive(),
    maxScreenshotCalls: z.number().int().nonnegative(),
    sessionTimeoutMs: z.number().int().positive(),
    stepWaitTimeoutMs: z.number().int().positive(),
  }).strict(),
  purposes: z.array(AcquisitionPurposeSchema).min(1),
  policyVersion: z.string().min(1).max(80),
  /** 费用展示：按既有路由价格，不改积分单价。 */
  costHints: z.array(z.object({
    stage: z.enum(["plan", "plan_next", "extraction", "screenshot_recognition", "task_verification"]),
    points: z.number().int().nonnegative(),
    note: z.string().max(200).optional(),
  }).strict()).max(12).default([]),
}).strict();
export type EvidenceCapabilities = z.infer<typeof EvidenceCapabilitiesSchema>;

/**
 * 上一次尝试为什么被拒（F2：提案被拒后的重试）。
 *
 * 服务端据此构造重试提示词，让模型知道"刚才错在哪"，而不是原地重抽一次。
 * 只允许 **拒绝码 + 简短理由 + 序号**：可用引用之类的信息一律由服务端从本次观察自己算，
 * 否则请求体就成了第二条注入通道（`.strict()` 会把多带的字段直接拒掉）。
 */
export const EvidencePreviousAttemptSchema = z.object({
  code: z.string().min(1).max(80),
  reason: z.string().min(1).max(300),
  attempt: z.number().int().min(1).max(8),
}).strict();
export type EvidencePreviousAttempt = z.infer<typeof EvidencePreviousAttemptSchema>;

/**
 * 建**服务端会话头**（P3-46）。
 *
 * 为什么需要它：服务端的会话头（预算与用途）原先**只在规划时**才建（`ensureHead` 由 `/plans` 调用）。
 * 于是任何"不经过模型规划"的固定过程（例如需求这条：确认 → 直接截图 → 识别）在服务端**根本不存在**，
 * 第一次 `/extractions` 就回 404「没有找到这个取数会话的可访问记录」——真机就是这样。
 * 会话头是**服务端资源**（预算的唯一权威），它的创建不该依赖一次模型调用。
 *
 * 不产生计费调用 ⇒ **不写计费回执**（回执只记录真实的计费调用）。
 * 预算语义与规划路径完全一致：重复建只收紧，不抬高（`LEAST`）。
 */
export const EvidenceSessionCreateRequestSchema = z.object({
  sessionId: z.string().min(1).max(160),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
  goal: AcquisitionGoalSchema,
  taskId: z.string().min(1).max(160).optional(),
  draftId: z.string().min(1).max(160).optional(),
}).strict();
export type EvidenceSessionCreateRequest = z.infer<typeof EvidenceSessionCreateRequestSchema>;

export const EvidenceSessionCreateResponseSchema = z.object({
  result: z.literal("ensured"),
  sessionId: z.string().min(1).max(160),
  revision: z.number().int().nonnegative(),
  state: z.string().min(1).max(40),
  remainingBudget: z.object({
    planningCalls: z.number().int().nonnegative(),
    readBatches: z.number().int().nonnegative(),
    screenshotCalls: z.number().int().nonnegative(),
    confirmedSegments: z.number().int().nonnegative(),
    modifiedSteps: z.number().int().nonnegative(),
  }).strict(),
}).strict();
export type EvidenceSessionCreateResponse = z.infer<typeof EvidenceSessionCreateResponseSchema>;

export const EvidencePlanRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  protocol: z.literal(EVIDENCE_ACQUISITION_PROTOCOL),
  goal: AcquisitionGoalSchema,
  taskId: z.string().min(1).max(160).optional(),
  draftId: z.string().min(1).max(160).optional(),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
  observation: PageObservationSchema,
  /** 会话范围确认记录；服务端据此复核，不信任裸 approved=true。 */
  sessionConsent: z.object({
    approved: z.literal(true),
    scopeHash: z.string().regex(/^[a-f\d]{64}$/),
    sites: z.array(z.string().min(1).max(240)).max(20).default([]),
    allowedActions: z.array(AcquisitionActionTypeSchema).max(ACQUISITION_ACTIONS.length).default([]),
    confirmedAt: z.string(),
  }).strict(),
  /** 上一次尝试被拒的原因；没有重试过时不带（旧客户端不受影响）。 */
  previousAttempt: EvidencePreviousAttemptSchema.optional(),
}).strict();
export type EvidencePlanRequest = z.infer<typeof EvidencePlanRequestSchema>;

export const EvidencePlanResponseSchema = z.object({
  sessionId: z.string().min(1).max(160),
  revision: z.number().int().nonnegative(),
  proposal: AcquisitionProposalSchema,
  proposalHash: z.string().regex(/^[a-f\d]{64}$/),
  /**
   * 只有 `interact` 段才带凭据：只有改变页面状态的段需要用户确认与单次领取。
   * `extract` 是本地只读，`wait` 不改变状态，`clarify`/`complete`/`stop` 不授予任何执行能力——
   * 给它们发一张"许可"会让"有凭据"看起来等于"可以做点什么"。
   */
  credential: EvidencePlanCredentialSchema.optional(),
  remainingBudget: AcquisitionBudgetUsageSchema.partial(),
  receipt: EvidenceCallReceiptSchema,
}).strict();
export type EvidencePlanResponse = z.infer<typeof EvidencePlanResponseSchema>;

export const EvidencePlanNextRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160),
  credential: EvidencePlanCredentialSchema,
  expectedRevision: z.number().int().nonnegative(),
  /** 上段实际结果摘要，不是模型声称的结果。 */
  lastSegmentResult: z.object({
    actionId: z.string().uuid(),
    outcome: z.enum(["succeeded", "failed", "unknown", "skipped"]),
    summary: z.string().max(600),
  }).strict(),
  observation: PageObservationSchema,
  /** 上一次尝试被拒的原因；同上。 */
  previousAttempt: EvidencePreviousAttemptSchema.optional(),
}).strict();
export type EvidencePlanNextRequest = z.infer<typeof EvidencePlanNextRequestSchema>;

/** 确认不调用模型：只验签并绑定该段参数。 */
export const EvidencePlanConfirmRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160),
  proposalHash: z.string().regex(/^[a-f\d]{64}$/),
  expectedRevision: z.number().int().nonnegative(),
  actionParameters: z.array(z.object({
    actionId: z.string().uuid(),
    parameterHash: z.string().regex(/^[a-f\d]{64}$/),
  }).strict()).min(1).max(ACQUISITION_ACTIONS.length),
  userConfirmation: z.object({ confirmed: z.literal(true), confirmedAt: z.string() }).strict(),
}).strict();
export type EvidencePlanConfirmRequest = z.infer<typeof EvidencePlanConfirmRequestSchema>;

export const EvidencePlanConfirmResponseSchema = z.object({
  result: z.literal("confirmed"),
  sessionId: z.string().min(1).max(160),
  revision: z.number().int().nonnegative(),
  confirmedParameterHash: z.string().regex(/^[a-f\d]{64}$/),
  expiresAt: z.string(),
}).strict();
export type EvidencePlanConfirmResponse = z.infer<typeof EvidencePlanConfirmResponseSchema>;

export const EvidenceActionAuthorizeRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160),
  credential: EvidencePlanCredentialSchema,
  actionId: z.string().uuid(),
  parameterHash: z.string().regex(/^[a-f\d]{64}$/),
  environmentId: z.string().min(1).max(120),
  deviceId: z.string().uuid(),
  /**
   * 动作本体由客户端随请求带来，服务端**不保存计划正文**（§11.2 只允许存动作哈希）。
   * 服务端会用已保存的哈希核对它没有被换过，再用请求里的当前现场做策略判定。
   */
  action: AcquisitionActionSchema,
  /** 当前相关现场：服务端据此复核目标是否仍然有效。 */
  observation: PageObservationSchema,
}).strict();
export type EvidenceActionAuthorizeRequest = z.infer<typeof EvidenceActionAuthorizeRequestSchema>;

export const EvidenceActionAuthorizeResultSchema = z.object({
  result: z.literal("claimed"),
  actionId: z.string().uuid(),
  sessionId: z.string().min(1).max(160),
  claimedAt: z.string(),
  expiresAt: z.string(),
}).strict();
export type EvidenceActionAuthorizeResult = z.infer<typeof EvidenceActionAuthorizeResultSchema>;

export const EvidenceExtractionRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160),
  purpose: AcquisitionPurposeSchema,
  expectedRevision: z.number().int().nonnegative(),
  /** 不可变输入摘要：共享确认针对它，服务端通过 Schema 与快照绑定复核。 */
  inputHash: z.string().regex(/^[a-f\d]{64}$/),
  goal: AcquisitionGoalSchema,
  observation: PageObservationSchema,
  textBlocks: z.array(ObservationTextBlockSchema).max(EVIDENCE_ACQUISITION_LIMITS.maxObservationBlocks).default([]),
  tables: z.array(ObservationTableSchema).max(20).default([]),
  /**
   * 用户确认共享的截图（§7.2、§11.3）。只有预览并确认之后才允许出现，
   * 所以这里带的是"已经过确认的图"，不是"模型想看的图"。
   *
   * `imageId` 是事实引用的锚点：来自截图的事实必须把 `location.kind` 写成
   * `image_region` 并指向这里的某个 `imageId`，否则服务端拒绝——位置合法也不能证明 OCR 正确（§7.3）。
   */
  images: z.array(z.object({
    imageId: z.string().min(1).max(160),
    mimeType: z.enum(["image/png", "image/jpeg"]),
    /** 已遮挡后的字节；原始未遮挡图不进入请求，也不落库。 */
    dataUrl: z.string().startsWith("data:image/"),
    /** 该图对应的采集时间与筛选状态：多张图不能只记一个（§7.2）。 */
    capturedAt: z.string(),
    filterState: z.array(z.string().max(160)).max(20).default([]),
    regionLabel: z.string().max(160).optional(),
  }).strict()).max(EVIDENCE_ACQUISITION_LIMITS.maxScreenshotsPerCall).default([]),
  /**
   * 这次识别要读的是**哪一种清单**（竞争 / 价格带 / 评论三张卡的固定过程）。
   *
   * - `trend`（默认）：图上的一条曲线/一组数值，不要求逐条；
   * - `product`：逐条商品（价格、已售）——竞争与价格带的结论是**算出来的**（阈值比较、分位）；
   * - `review_tags`：逐条评价标签（标签原文 + 正负向 + 条数）——评论那条按正负向汇总。
   *
   * 为什么由请求声明而不是服务端从目标里猜：提示词要据此产出**机器可读的逐条事实**，
   * 而"从目标文字猜要哪种清单"会在目标措辞变化时静默退化成一段描述性文字——
   * 结论随即永远是空的（描述性文字没法做统计）。
   * 字段词表不在这里（它属于提示词，见 `MARKETPLACE_LIST_FIELDS` / `REVIEW_TAG_FIELDS`）。
   */
  listKind: z.enum(["trend", "product", "review_tags"]).default("trend"),
  /**
   * 这份商品清单**要不要详情页链接**（只有评论那条"逐个进详情"需要）。
   *
   * 默认不要：竞争/价格带只用价格与已售，把链接一起问了会把输出拉长近一半——
   * 真机 2026-09-18 就因此整段被拒过（`OUTPUT_INVALID`，115 秒的视觉调用白费）。
   */
  needsProductLinks: z.boolean().default(false),
  /**
   * 每张图**是什么**（按 `images` 的顺序），例如 `["结果1：默认排序", "结果2：按销量排序"]`。
   *
   * 两张图**一次调用**时（用户 2026-09-18 要求）模型必须能分清哪张对应哪一份结果：
   * 读数里的 `i` 就是这里的序号。只发一张图时留空。
   */
  imageLabels: z.array(z.string().trim().min(1).max(80)).max(2).default([]),
}).strict().superRefine((value, context) => {
  if (value.images.length === 0) return;
  const ids = value.images.map((image) => image.imageId);
  if (new Set(ids).size !== ids.length) {
    context.addIssue({ code: "custom", path: ["images"], message: "截图标识重复" });
  }
});
export type EvidenceExtractionRequest = z.infer<typeof EvidenceExtractionRequestSchema>;

/**
 * §7.3：截图来源的事实必须指向**本次共享的** `imageId`，且精度只能是 `confirmed_ocr`。
 *
 * 这条校验刻意放在契约层：服务端要拿它拒绝"从图里推出经营事实"，客户端也要在把 bundle
 * 写进本地记录前用同一套规则挡一次。位置合法不代表 OCR 正确，所以这里只校验来源归属与精度，
 * 不假装能验证识别质量。
 */
export function validateScreenshotFactReferences(
  bundle: Pick<EvidenceBundle, "facts" | "sources">,
  imageIds: readonly string[],
): { ok: true } | { ok: false; reason: string } {
  const allowed = new Set(imageIds);
  for (const source of bundle.sources) {
    if (source.location.kind !== "image_region") continue;
    if (!allowed.has(source.location.imageId)) return { ok: false, reason: "截图来源引用了本次没有共享的图片" };
  }
  for (const fact of bundle.facts) {
    if (fact.origin !== "screenshot") {
      // 非截图来源不得借用图片区域作为出处：否则"页面明示值"看起来像有图作证。
      if (fact.sourceRef === undefined) continue;
      const source = bundle.sources.find((item) => item.sourceRef === fact.sourceRef);
      if (source?.location.kind === "image_region") return { ok: false, reason: "非截图事实引用了图片区域" };
      continue;
    }
    if (fact.precision !== "confirmed_ocr") return { ok: false, reason: "截图来源的事实必须标为已确认 OCR" };
    if (fact.sourceRef === undefined) return { ok: false, reason: "截图来源的事实必须带出处" };
    const source = bundle.sources.find((item) => item.sourceRef === fact.sourceRef);
    if (!source || source.location.kind !== "image_region") return { ok: false, reason: "截图来源的事实必须指向图片区域" };
    if (!allowed.has(source.location.imageId)) return { ok: false, reason: "引用了本次没有共享的图片" };
  }
  return { ok: true };
}

export const EvidenceRecipeReadProgressSchema = z.object({
  advanced: z.boolean(), completed: z.boolean(), stepIndex: z.number().int().nonnegative().optional(),
}).strict();
export type EvidenceRecipeReadProgress = z.infer<typeof EvidenceRecipeReadProgressSchema>;

/** A local, read-only result reports evidence, never fabricates an interaction credential. */
export const EvidenceRecipeReadResultSchema = z.object({
  requestId: z.string().min(1).max(160), sessionId: z.string().min(1).max(160),
  expectedRevision: z.number().int().nonnegative(), observation: PageObservationSchema, bundle: EvidenceBundleSchema,
}).strict();
export type EvidenceRecipeReadResult = z.infer<typeof EvidenceRecipeReadResultSchema>;

export const EvidenceExtractionResponseSchema = z.object({
  bundle: EvidenceBundleSchema,
  receipt: EvidenceCallReceiptSchema,
  configuredRecipeProgress: EvidenceRecipeReadProgressSchema.optional(),
}).strict();
export type EvidenceExtractionResponse = z.infer<typeof EvidenceExtractionResponseSchema>;

/**
 * P3-2：开放检索请求。它是一次**计费调用**（外部付费接口），所以带 `requestId` 以便留回执。
 *
 * 刻意**不带会话目标/站点范围**：检索词由规划器给，服务端不替它扩写，
 * 也不因为"这次取数只允许某些站点"就偷偷加限定——那会让检索结果与用户看到的查询不是一回事。
 */
export const EvidenceWebSearchRequestSchema = z.object({
  requestId: z.string().min(1).max(160),
  query: z.string().trim().min(1).max(300),
  maxResults: z.number().int().min(1).max(8).default(5),
}).strict();
export type EvidenceWebSearchRequest = z.infer<typeof EvidenceWebSearchRequestSchema>;

/**
 * 检索结果：**只有线索**（标题 + 链接）。
 *
 * R13：它是开放网络的内容，**不是**官方政策证据。所以这里刻意只回这两个字段——
 * 正文摘要一律不回，避免下游把它当"读到的内容"用。
 */
export const EvidenceWebSearchResponseSchema = z.object({
  leads: z.array(z.object({
    title: z.string().max(200),
    url: z.string().max(2_048),
  }).strict()).max(8),
  /**
   * 计费回执**可选**：回执本身已经落进 `evidence_call_receipts`（这是不可省的），
   * 客户端要明细可以按 `requestId` 查既有端点 `/calls/:requestId`。
   * 不在这里回，是为了不让"响应里有没有回执"变成计费留痕的前提。
   */
  receipt: EvidenceCallReceiptSchema.optional(),
}).strict();
export type EvidenceWebSearchResponse = z.infer<typeof EvidenceWebSearchResponseSchema>;

/** 字段采用只改插件草稿，因此单独成一个本地请求形状，不会出现在授权接口里。 */
export const EvidenceFieldAdoptionRequestSchema = z.object({
  sessionId: z.string().min(1).max(160),
  draftRevision: z.number().int().nonnegative(),
  adoptions: z.array(FieldAdoptionSchema).max(200),
}).strict();
export type EvidenceFieldAdoptionRequest = z.infer<typeof EvidenceFieldAdoptionRequestSchema>;

/* -------------------------------------------------------------------------- */
/* 模型输出 Schema：由契约层唯一生成，Prompt 与校验共用一份                       */
/* -------------------------------------------------------------------------- */

let proposalJsonSchema: unknown;
let bundleJsonSchema: unknown;

/**
 * 模型输出的 JSON Schema 只在这里生成。
 *
 * 放在契约层而不是 Prompt 层，是因为 Prompt 包没有（也不需要）zod 依赖：
 * 让 Prompt 自己生成 Schema，就会出现"契约改了、Prompt 里那份没改"的漂移；
 * 而生成是惰性的，浏览器侧调用不到就不会付出这份开销。
 */
export function acquisitionProposalJsonSchema(): unknown {
  return (proposalJsonSchema ??= z.toJSONSchema(AcquisitionProposalSchema));
}

export function evidenceBundleJsonSchema(): unknown {
  return (bundleJsonSchema ??= z.toJSONSchema(EvidenceBundleSchema));
}

/* -------------------------------------------------------------------------- */
/* 参数哈希的规范序：唯一所有者                                                 */
/* -------------------------------------------------------------------------- */

/**
 * 键排序、去掉 `undefined` 的确定性序列化；数组保序。
 *
 * 为什么不各自 `JSON.stringify`：对象键顺序取决于调用方怎么构造，
 * 同一份参数换个写法就会算出两个哈希。而这里的哈希是**授权绑定**——
 * 服务端用规划时保存的哈希核对客户端带来的动作，两边只要有一点不一致，
 * 要么合法动作被拒、要么"换了参数还能沿用旧许可"，两种都很难在联调时发现。
 * 所以规范序只在这里定义，浏览器与服务器都从这里取。
 */
export function stableSerialize(value: unknown): string {
  return JSON.stringify(stableValue(value));
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      if (source[key] === undefined) continue;
      out[key] = stableValue(source[key]);
    }
    return out;
  }
  return value;
}

/**
 * 单个动作的规范序文本：参与哈希的是"标识 + 类型 + 动作本体"。
 *
 * 动作本体**包含** `reason`（模型给用户看的说明）。这是刻意的严格选择：
 * 用户在确认卡上看到的就是"对象 + 参数 + 说明"，任何一项被换掉都应当让旧许可失效，
 * 而不是让"参数没变"成为"说明可以随便改"的理由。所以哈希覆盖整个动作，不做字段豁免。
 */
export function evidenceActionCanonical(action: AcquisitionAction): string {
  return stableSerialize({ actionId: action.actionId, type: action.type, action });
}
